"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { format, isBefore, isValid } from "date-fns";
import { memo, useContext, useEffect, useMemo, useRef } from "react";
import { isDateRange, useDayPicker } from "react-day-picker";
import { CdnIcon } from "../../CdnIcon/index.js";
import { calendarVariants } from "../Calendar/Calendar.variants.js";
import { DayMapContext } from "../Providers/DayMapProvider.js";
const CALENDAR_DAY_FORMAT = 'yyyyMMdd';
const PRICE_THRESHOLDS = {
    EXCELLENT: 50,
    GOOD: 100
};
function getPriceRange(formattedPrice, lowestPrice) {
    if (!lowestPrice || !formattedPrice) return 'GOOD';
    if (formattedPrice < lowestPrice + PRICE_THRESHOLDS.EXCELLENT) return 'EXCELLENT';
    if (formattedPrice < lowestPrice + PRICE_THRESHOLDS.GOOD) return 'GOOD';
    if (formattedPrice > lowestPrice) return 'HIGH';
    return 'GOOD';
}
function isDateDisabled(date, disabled) {
    if ('object' == typeof disabled && null !== disabled && 'before' in disabled) {
        const beforeDate = disabled.before;
        return isValid(beforeDate) && isBefore(date, beforeDate);
    }
    return false;
}
function getSelectedDateString(mode, selected) {
    if (!selected) return '';
    if ('range' === mode && isDateRange(selected) && selected.from && isValid(selected.from)) return format(selected.from, CALENDAR_DAY_FORMAT);
    if ('single' === mode && isValid(selected)) return format(selected, CALENDAR_DAY_FORMAT);
    return '';
}
function renderSubText(variant, formattedPrice, selectedPrice) {
    switch(variant){
        case 'dayHeatMap':
            return formattedPrice ? /*#__PURE__*/ jsxs("span", {
                className: "text-body3",
                children: [
                    "$",
                    formattedPrice
                ]
            }) : null;
        case 'dayBelowAvg':
            return selectedPrice && formattedPrice && formattedPrice < selectedPrice ? /*#__PURE__*/ jsx("span", {
                className: "text-success-8 text-label",
                children: "⏺"
            }) : null;
        case 'dayIcon':
            return /*#__PURE__*/ jsx(CdnIcon, {
                iconName: "chevron_right",
                size: "20"
            });
        default:
            return null;
    }
}
function HeatMapDayButtonComponent(props) {
    const { day, modifiers, ...buttonProps } = props;
    const dayMapContext = useContext(DayMapContext);
    const { dayMap, mode, isMobile } = dayMapContext || {};
    const { selected, dayPickerProps } = useDayPicker();
    const { disabled } = dayPickerProps;
    const { lowestPrice, variant = 'day', data: dayMapData } = dayMap || {};
    const ref = useRef(null);
    const dateInfo = useMemo(()=>({
            currentDate: format(day.date, 'd'),
            formattedPriceDate: format(day.date, CALENDAR_DAY_FORMAT),
            isDisabled: isDateDisabled(day.date, disabled)
        }), [
        day.date,
        disabled
    ]);
    const selectedDate = useMemo(()=>getSelectedDateString(mode, selected), [
        mode,
        selected
    ]);
    const pricingInfo = useMemo(()=>{
        if (!dayMapData || dateInfo.isDisabled) return {
            formattedPrice: void 0,
            selectedPrice: void 0
        };
        return {
            formattedPrice: dayMapData[dateInfo.formattedPriceDate]?.price,
            selectedPrice: selectedDate ? dayMapData[selectedDate]?.price : void 0
        };
    }, [
        dayMapData,
        dateInfo.formattedPriceDate,
        dateInfo.isDisabled,
        selectedDate
    ]);
    const subText = useMemo(()=>dayMapData && !dateInfo.isDisabled ? renderSubText(variant, pricingInfo.formattedPrice, pricingInfo.selectedPrice) : null, [
        dayMapData,
        dateInfo.isDisabled,
        variant,
        pricingInfo.formattedPrice,
        pricingInfo.selectedPrice
    ]);
    useEffect(()=>{
        if (modifiers.focused) ref.current?.focus();
    }, [
        modifiers.focused
    ]);
    const isDayMap = !!dayMap?.data;
    const colorVariant = 'dayHeatMap' === variant && !dateInfo.isDisabled && isDayMap ? getPriceRange(pricingInfo.formattedPrice || 0, lowestPrice || 0) : 'DEFAULT';
    const { dayButton } = calendarVariants({
        isMobile,
        heatMapVariant: variant,
        colorVariant
    });
    return /*#__PURE__*/ jsxs("button", {
        ...buttonProps,
        ref: ref,
        className: dayButton(),
        "data-testid": "heat-map-day-button",
        children: [
            /*#__PURE__*/ jsx("span", {
                children: dateInfo.currentDate
            }),
            subText
        ]
    });
}
const HeatMapDayButton = /*#__PURE__*/ memo(HeatMapDayButtonComponent);
export { HeatMapDayButton };
